import React from "react";
import { Link } from "react-router";
import { AlertTriangle, CheckCircle2, Loader2, ScrollText } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { AuditLogType, OpenAuditLogState } from "@/models/AuditLog";
import { ConflictRuleProgress, ConflictRuleRunResult } from "@/models/ConflictRule";
import RuleOutcomeSummary from "./RuleOutcomeSummary";

/**
 * Where a run stands: streaming its chunks, finished, or stopped by an error after it started. `refreshing` is set
 * while the organization's statistics are recalculated afterwards, which the counts elsewhere wait for.
 */
export type RuleRunState =
  | { status: "running"; confirmedFileCount: number; progress: ConflictRuleProgress | null }
  | { status: "finished"; result: ConflictRuleRunResult; refreshing: boolean }
  | { status: "failed"; error: string; refreshing: boolean };

/**
 * Audit Logs filtered to rule runs, with the run's own entry open when its id is known. A run stopped by an error
 * reports no id, so its link opens the filtered list.
 */
const RunAuditLink: React.FC<{ organizationId: string; auditLogId?: string; children: React.ReactNode }> = ({
  organizationId,
  auditLogId,
  children,
}) => {
  const state: OpenAuditLogState = { eventTypes: [AuditLogType.ConflictRuleRun], auditLogId };
  return (
    <Link
      to={`/organization/${organizationId}/audit-logs`}
      state={state}
      className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
    >
      <ScrollText className="size-3.5" />
      {children}
    </Link>
  );
};

const Refreshing: React.FC = () => (
  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
    <Loader2 className="size-3.5 animate-spin" />
    Refreshing statistics…
  </span>
);

/**
 * A rule run as it happens and once it is over. Every figure while it runs counts files already committed; a run that
 * stops part-way keeps what committed, and its audit entry says how far it got.
 */
const RuleRunPanel: React.FC<{ run: RuleRunState; organizationId: string }> = ({ run, organizationId }) => {
  if (run.status === "running") {
    const { progress, confirmedFileCount } = run;
    const filesDone = progress?.filesDecided ?? 0;
    const percent = confirmedFileCount > 0 ? Math.min(100, (filesDone / confirmedFileCount) * 100) : 0;
    return (
      <div className="flex flex-col gap-2 rounded-md border border-primary/40 bg-primary/5 p-4">
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <span className="font-medium">Running the rule…</span>
          <span className="ml-auto font-mono text-xs text-muted-foreground">{Math.round(percent)}%</span>
        </div>
        <Progress value={percent} className="h-2" />
        <span className="text-xs text-muted-foreground">
          {progress
            ? `${filesDone.toLocaleString()} of ${confirmedFileCount.toLocaleString()} files decided · ` +
              `${progress.parentsDone.toLocaleString()} of ${progress.parentCount.toLocaleString()} entities done`
            : "Checking every group again before the first decision…"}
        </span>
        <span className="text-xs text-muted-foreground">
          Keep this tab open until the run finishes. Closing it stops the run; what it decided until then stays
          decided.
        </span>
      </div>
    );
  }

  if (run.status === "failed") {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="size-4" />
        <AlertTitle>The run stopped before it finished</AlertTitle>
        <AlertDescription className="flex flex-col gap-1">
          <span>{run.error}</span>
          <span>
            What it decided until then stays decided. Its{" "}
            <RunAuditLink organizationId={organizationId}>Conflict Rule Run</RunAuditLink> entry in Audit Logs records
            what committed. Preview again to decide the rest.
          </span>
          {run.refreshing && <Refreshing />}
        </AlertDescription>
      </Alert>
    );
  }

  const { result } = run;
  return (
    <div className="flex flex-col gap-4 rounded-md border bg-card p-4">
      <div className="flex flex-col gap-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <CheckCircle2 className="size-4 text-emerald-600" />
          Run finished
        </h3>
        <p className="text-xs text-muted-foreground">
          Recorded with every decided file and every skipped group in its{" "}
          <RunAuditLink organizationId={organizationId} auditLogId={result.auditLogId}>
            audit entry
          </RunAuditLink>
          .
        </p>
        {run.refreshing && <Refreshing />}
      </div>
      <RuleOutcomeSummary
        tense="run"
        groupsDecided={result.groupsDecided}
        filesDecided={result.filesDecided}
        copiesThatWait={result.copiesThatWait}
        groupsSkipped={result.groupsSkipped}
        actions={result.actions}
        skipped={result.skipped}
      />
    </div>
  );
};

export default RuleRunPanel;
